import { PageController } from '@client/components/_core/layout/PageController';
import { PendingReserveChangeCallout } from '@client/components/claims/financials/PendingReserveChangeCallout';

import { useFormatter } from '@client/hooks/utils/useFormatter';
import { ICONS } from '@client/styles/theme';
import { trpc } from '@client/trpc/client';
import { cn } from '@client/utils';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Card, Col, Row, Tooltip } from 'antd';
import { Decimal } from 'decimal.js';
import { CircleQuestionMark, Wallet } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { match } from 'ts-pattern';
import { claimFormStore } from '../store';

// Lazy-load the heavy list components so each lands in its own chunk and does
// not bloat this route's entry. Without splitting, the antd Table + tRPC query
// machinery inside each list dominated the financial route's import graph.
const CompensationList = lazy(() =>
    import('@client/components/claims/financials/CompensationList').then((m) => ({ default: m.CompensationList })),
);
const ExcessList = lazy(() =>
    import('@client/components/claims/financials/ExcessList').then((m) => ({ default: m.ExcessList })),
);
const ReserveList = lazy(() =>
    import('@client/components/claims/financials/ReserveList').then((m) => ({ default: m.ReserveList })),
);

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials')({
    component: FinancialsPage,
});

function FinancialsPage() {
    return (
        <PageController route={Route}>
            <Financials />
        </PageController>
    );
}

type Entity = 'reserve' | 'excess';
// type Entity = 'reserve' | 'excess' | 'recovery';

const config: Record<
    Entity,
    {
        icon: typeof ICONS.reserve;
        title: string;
        receivedLabel: string;
        outstandingLabel: string;
    }
> = {
    reserve: {
        icon: ICONS.reserve,
        title: 'Reserve',
        receivedLabel: 'Paid',
        outstandingLabel: 'Remaining',
    },
    excess: {
        icon: ICONS.excess,
        title: 'Excess',
        receivedLabel: 'Received',
        outstandingLabel: 'Outstanding',
    },
    // recovery: {
    //     icon: ICONS.recovery,
    //     title: 'Recovery',
    //     receivedLabel: 'Received',
    // },
};

const zeroDecimal = new Decimal(0);

// A zero or not-applicable balance carries no signal, so only a positive amount takes the tone.
const toneWhenPositive = (amount: Decimal, tone: string) => (amount.gt(0) ? tone : 'text-muted-foreground');
const dotWhenPositive = (amount: Decimal, tone: string) => (amount.gt(0) ? tone : 'bg-muted-foreground');

function FinancialEntitySummary({
    entity,
    isLoading,
    totalAmount: totalAmountProp,
    receivedAmount: receivedAmountProp,
}: {
    entity: Entity;
    isLoading: boolean;
    totalAmount: Decimal | undefined;
    receivedAmount: Decimal | undefined;
}) {
    const { formatCurrency } = useFormatter();

    const { icon: Icon, title, receivedLabel, outstandingLabel } = config[entity];
    const totalAmount = totalAmountProp || zeroDecimal;
    const receivedAmount = receivedAmountProp || zeroDecimal;
    const outstanding = Decimal.max(0, totalAmount.minus(receivedAmount));

    return (
        <Card
            size="small"
            loading={isLoading}
            className="h-full"
            classNames={{
                title: 'gap-2 items-center flex',
                body: 'space-y-3',
            }}
            title={
                <>
                    <Icon className="card-title-icon" />
                    {title}
                </>
            }
        >
            <div className={cn('-mx-3 -mt-3 flex flex-col gap-1', 'border-gray-200 border-b px-4 py-3')}>
                <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">Total</span>
                <span className="font-medium text-xl">{formatCurrency(totalAmount)}</span>
            </div>
            <div className="flex justify-between gap-2">
                <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                        <div className={cn('size-2 rounded-full', dotWhenPositive(receivedAmount, 'bg-green-500'))} />
                        <span className="text-muted-foreground text-xs">{receivedLabel}</span>
                    </div>
                    <div className={cn('font-medium text-base', toneWhenPositive(receivedAmount, 'text-green-600'))}>
                        {formatCurrency(receivedAmount)}
                    </div>
                </div>
                <div className="space-y-1 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                        <span className="text-muted-foreground text-xs">{outstandingLabel}</span>
                        <div className={cn('size-2 rounded-full', dotWhenPositive(outstanding, 'bg-orange-400'))} />
                    </div>
                    <div className={cn('font-medium text-base', toneWhenPositive(outstanding, 'text-orange-400'))}>
                        {formatCurrency(outstanding)}
                    </div>
                </div>
            </div>
        </Card>
    );
}

function Financials() {
    const claim = claimFormStore.use.formData();
    const isReadonly = claimFormStore.use.isReadonly();
    const { data, isPending } = trpc.claim.getFinancialSummary.useQuery({ claimId: claim.id });
    const { data: financialSections } = trpc.financialSection.listFinancialSections.useQuery(
        claim.productTypeId
            ? {
                  where: {
                      productTypeId: claim.productTypeId,
                  },
              }
            : skipToken,
    );
    const { formatCurrency } = useFormatter();
    const netCost = Decimal.max(
        0,
        (data?.totalReserveAmount || zeroDecimal).minus(data?.recoveryAmount || 0).minus(data?.excessAmount || 0),
    );

    return (
        <>
            <Outlet />
            <PendingReserveChangeCallout claimId={claim.id} />
            <Row gutter={[16, 16]}>
                <Col span={6}>
                    <Card
                        size="small"
                        loading={isPending}
                        className="h-full"
                        classNames={{
                            title: 'gap-2 items-center flex',
                            body: 'space-y-3',
                        }}
                        title={
                            <>
                                <Wallet className="card-title-icon" />
                                Cost incurred
                            </>
                        }
                    >
                        <div className="flex flex-col gap-0.5">
                            <span
                                className={cn(
                                    'flex items-center gap-1',
                                    'font-medium text-muted-foreground text-xs uppercase tracking-wide',
                                )}
                            >
                                Total
                                <Tooltip title="The total cost incurred for the claim. This is the reserve amount.">
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </span>
                            <span className="truncate font-semibold text-xl">
                                {formatCurrency(Decimal.max(0, data?.totalReserveAmount || 0))}
                            </span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <div
                                className={cn(
                                    'flex items-center gap-1',
                                    'font-medium text-muted-foreground text-xs uppercase tracking-wide',
                                )}
                            >
                                Net
                                <Tooltip title="The net cost incurred for the claim. This is the total cost incurred minus the recovery and excess amounts.">
                                    <CircleQuestionMark className="size-4 cursor-help text-subtle-foreground" />
                                </Tooltip>
                            </div>
                            <span
                                className={cn(
                                    'truncate font-semibold text-xl',
                                    toneWhenPositive(netCost, 'text-green-600'),
                                )}
                            >
                                {formatCurrency(netCost)}
                            </span>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <FinancialEntitySummary
                        entity="reserve"
                        isLoading={isPending}
                        totalAmount={data?.totalReserveAmount}
                        receivedAmount={data?.compensationAmount}
                    />
                </Col>
                <Col span={6}>
                    <FinancialEntitySummary
                        entity="excess"
                        isLoading={isPending}
                        totalAmount={data?.excessAmount}
                        receivedAmount={data?.receivedExcessAmount}
                    />
                </Col>
                {/* <Col span={6}>
                    <FinancialEntitySummary entity="recovery" isLoading={isPending} totalAmount={data?.recoveryAmount} receivedAmount={data?.receivedRecoveryAmount} />
                </Col> */}

                {financialSections?.rows.map(({ title, internalName }) => (
                    <Col span={24} key={internalName}>
                        <Suspense fallback={<Card loading size="small" title={title} />}>
                            {match(internalName)
                                .with('EXCESS', () => (
                                    <ExcessList title={title} claimId={claim.id} isReadOnly={isReadonly} />
                                ))
                                .with('RESERVE', () => (
                                    <ReserveList title={title} claimId={claim.id} isReadOnly={isReadonly} />
                                ))
                                .with('INDEMNITY_AND_EXPENSE', () => (
                                    <CompensationList title={title} claimId={claim.id} isReadOnly={isReadonly} />
                                ))
                                .otherwise(() => null)}
                        </Suspense>
                    </Col>
                ))}
            </Row>
        </>
    );
}
